/**
 * Signup hand-off — the web client gets a one-shot token, displays a QR for
 * the user's phone, and waits for the phone to register a passkey.
 *
 * - initHandoff() returns the token + URL (use the URL to render the QR).
 * - subscribe() polls /api/passkey/list every 5s (the user is already
 *   JWT-authed from the post-OTP session).
 */

import { apiUrl } from "@/lib/api";
import { getAccessToken } from "@/lib/auth";

export type HandoffSession = {
  token: string;
  handoffUrl: string;
  expiresIn: number;
};

export async function initHandoff(): Promise<HandoffSession> {
  const accessToken = await getAccessToken();
  if (!accessToken) throw new Error("Niet ingelogd voor handoff");

  const resp = await fetch(apiUrl("/api/passkey/handoff/init"), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${accessToken}`,
    },
  });
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `handoff/init ${resp.status}`);
  }
  const json = (await resp.json()) as {
    token: string;
    handoff_url: string;
    expires_in: number;
  };
  return {
    token: json.token,
    handoffUrl: json.handoff_url,
    expiresIn: json.expires_in,
  };
}

export type HandoffCallbacks = {
  onRegistered: (userId: string) => void;
  onExpired: () => void;
};

/**
 * Poll /api/passkey/list every 5s until the handoff completes. One row
 * appearing for the currently-signed-in user (JWT-authed) proves it.
 *
 * The timeoutMs guard fires onExpired locally — the token will already have
 * expired server-side at that point.
 */
export function subscribeHandoff(
  session: HandoffSession,
  cbs: HandoffCallbacks,
  timeoutMs = session.expiresIn * 1000,
): () => void {
  let settled = false;
  const finish = (fn: () => void) => {
    if (settled) return;
    settled = true;
    cleanup();
    fn();
  };

  const pollTimer = window.setInterval(async () => {
    if (settled) return;
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return;
      const resp = await fetch(apiUrl("/api/passkey/list"), {
        headers: { authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) return;
      const rows = (await resp.json()) as Array<{ id: string }>;
      if (rows.length > 0) {
        finish(() => cbs.onRegistered(""));
      }
    } catch {
      /* network blip */
    }
  }, 5000);

  const expireTimer = window.setTimeout(() => {
    finish(cbs.onExpired);
  }, timeoutMs);

  function cleanup() {
    window.clearInterval(pollTimer);
    window.clearTimeout(expireTimer);
  }

  return cleanup;
}
